import { useRef, useState, type ReactNode } from 'react';
import { Camera, Image as ImageIcon, Trash2 } from 'lucide-react';
import { useApi, useApp } from '@/shared/app-context';
import { errorMessage } from '@/shared/http/http-client';
import { preparePhoto } from '@/shared/photo/prepare-photo';
import { Avatar, Button, ErrorText, Muted } from '@/shared/ui';
import styles from './avatar-field.module.scss';

/**
 * La photo de profil.
 *
 * Deux entrées, comme pour la bouteille : `capture="user"` ouvre l'appareil frontal du
 * téléphone — un avatar se prend en se regardant, pas en visant devant soi —, le second
 * bouton ouvre la photothèque, pour la photo qu'on a déjà et pour l'ordinateur qui n'a pas
 * d'appareil.
 *
 * L'envoi part dès la sélection : demander une confirmation après avoir déclenché
 * l'appareil ajouterait un geste sans rien décider de plus.
 *
 * Sans photo, l'initiale du pseudo reste — c'est ce que fait `Avatar` partout ailleurs, et
 * ce vers quoi « Retirer » ramène.
 */
export function AvatarField(): ReactNode {
  const api = useApi();
  const { member, setMember } = useApp();
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Remplacer un avatar efface l'ancien fichier : une balise qui portait encore son URL la
  // redemande et reçoit un 404. On retombe alors sur l'initiale plutôt qu'une image cassée.
  const [broken, setBroken] = useState(false);

  if (!member) return null;
  // Le rétrécissement de type ne franchit pas la frontière d'une fonction : on le fige ici
  // pour que les deux fermetures ci-dessous manipulent un membre, pas un membre peut-être nul.
  const me = member;

  async function upload(original: File | undefined): Promise<void> {
    if (!original) return;

    setBusy(true);
    setError(null);
    try {
      // Conversion avant tout le reste : un iPhone envoie du HEIC de quatre mégaoctets, que
      // l'API refuse deux fois — sur le format et sur le poids.
      const file = await preparePhoto(original);
      const updated = await api.members.setAvatar(file);
      setBroken(false);
      // Seul l'avatar a changé : on recompose le membre courant plutôt que de redemander
      // la session entière, dont le rôle et les capacités n'ont pas bougé.
      setMember({ ...me, avatarUrl: updated.avatarUrl });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
      // Sans cela, reprendre deux fois la même photo n'émettrait pas de second `change`.
      if (cameraRef.current) cameraRef.current.value = '';
      if (libraryRef.current) libraryRef.current.value = '';
    }
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.members.removeAvatar();
      setMember({ ...me, avatarUrl: updated.avatarUrl });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  const shown = broken ? null : me.avatarUrl;

  return (
    <div className={styles.field}>
      <div className={styles.preview}>
        {shown ? (
          // `Avatar` ne sait pas dire qu'une image a cassé : on garde la balise ici pour
          // capter l'erreur, et on lui repasse la main dès qu'il n'y a plus d'URL.
          <img className={styles.image} src={shown} alt="" onError={() => setBroken(true)} />
        ) : (
          <Avatar pseudo={me.pseudo} size="lg" />
        )}
      </div>

      {/* Les champs restent hors flux : ce sont les boutons qui les déclenchent. */}
      <input
        ref={cameraRef}
        className={styles.hidden}
        type="file"
        accept="image/*"
        capture="user"
        onChange={(event) => void upload(event.target.files?.[0])}
      />
      <input
        ref={libraryRef}
        className={styles.hidden}
        type="file"
        accept="image/*"
        onChange={(event) => void upload(event.target.files?.[0])}
      />

      <div className={styles.actions}>
        <Button variant="secondary" disabled={busy} onClick={() => cameraRef.current?.click()}>
          <Camera size={17} strokeWidth={1.5} aria-hidden="true" />
          {busy ? 'Envoi…' : me.avatarUrl ? 'Reprendre' : 'Se photographier'}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => libraryRef.current?.click()}>
          <ImageIcon size={17} strokeWidth={1.5} aria-hidden="true" />
          Choisir un fichier
        </Button>
        {me.avatarUrl ? (
          <Button variant="ghost" disabled={busy} onClick={() => void remove()}>
            <Trash2 size={17} strokeWidth={1.5} aria-hidden="true" />
            Retirer
          </Button>
        ) : null}
      </div>

      <ErrorText>{error}</ErrorText>
      <Muted>
        La photo est réduite avant l’envoi, et le lieu de la prise de vue en est retiré avant
        son enregistrement. Elle n’est visible que des membres du cercle.
      </Muted>
    </div>
  );
}
